import { z } from "zod";

// TMDB API Types and Schemas
export const TMDBSearchResult = z.object({
  id: z.number(),
  title: z.string(),
  overview: z.string().optional(),
  release_date: z.string().optional(),
  poster_path: z.string().optional().nullable(),
  backdrop_path: z.string().optional().nullable(),
  vote_average: z.number().optional(),
  genre_ids: z.array(z.number()).optional(),
});

export const TMDBGenre = z.object({
  id: z.number(),
  name: z.string(),
});

export const TMDBProductionCompany = z.object({
  id: z.number(),
  name: z.string(),
  logo_path: z.string().optional().nullable(),
  origin_country: z.string(),
});

export const TMDBMovieDetails = z.object({
  id: z.number(),
  title: z.string(),
  overview: z.string().optional(),
  release_date: z.string().optional(),
  runtime: z.number().optional().nullable(),
  poster_path: z.string().optional().nullable(),
  backdrop_path: z.string().optional().nullable(),
  genres: z.array(TMDBGenre),
  production_companies: z.array(TMDBProductionCompany),
  vote_average: z.number().optional(),
  vote_count: z.number().optional(),
});

export const TMDBCastMember = z.object({
  id: z.number(),
  name: z.string(),
  character: z.string().optional(),
  order: z.number().optional(),
  profile_path: z.string().optional().nullable(),
});

export const TMDBCrewMember = z.object({
  id: z.number(),
  name: z.string(),
  job: z.string(),
  department: z.string(),
  profile_path: z.string().optional().nullable(),
});

export const TMDBCredits = z.object({
  id: z.number(),
  cast: z.array(TMDBCastMember),
  crew: z.array(TMDBCrewMember),
});

export const TMDBSearchResponse = z.object({
  page: z.number(),
  results: z.array(TMDBSearchResult),
  total_pages: z.number(),
  total_results: z.number(),
});

// Rate limiting helper
class RateLimiter {
  private requests: number[] = [];
  private readonly maxRequests = 40; // TMDB allows 40 requests per 10 seconds
  private readonly windowMs = 10 * 1000; // 10 seconds

  async waitIfNeeded(): Promise<void> {
    const now = Date.now();

    // Remove requests older than the window
    this.requests = this.requests.filter((time) => now - time < this.windowMs);

    if (this.requests.length >= this.maxRequests) {
      const oldestRequest = Math.min(...this.requests);
      const waitTime = this.windowMs - (now - oldestRequest);

      if (waitTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, waitTime));
      }
    }

    this.requests.push(now);
  }
}

export interface TMDBClient {
  searchMovie(query: string): Promise<z.infer<typeof TMDBSearchResult>[]>;
  getMovieDetails(tmdbId: number): Promise<z.infer<typeof TMDBMovieDetails>>;
  getMovieCredits(tmdbId: number): Promise<z.infer<typeof TMDBCredits>>;
  getImageUrl(path: string, size?: "w300" | "w500" | "w780" | "w1280" | "original"): string;
}

export function createTMDBClient(apiKey: string): TMDBClient {
  const baseUrl = "https://api.themoviedb.org/3";
  const imageBaseUrl = "https://image.tmdb.org/t/p";
  const rateLimiter = new RateLimiter();

  async function tmdbFetch<T>(path: string, schema: z.ZodType<T>): Promise<T> {
    await rateLimiter.waitIfNeeded();

    const url = `${baseUrl}${path}${path.includes("?") ? "&" : "?"}api_key=${apiKey}`;

    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`TMDB API error: ${response.status} ${response.statusText}`);
    }

    const data = (await response.json()) as unknown;
    return schema.parse(data);
  }

  return {
    async searchMovie(query: string) {
      const response = await tmdbFetch(
        `/search/movie?query=${encodeURIComponent(query)}&include_adult=false`,
        TMDBSearchResponse,
      );
      return response.results;
    },

    async getMovieDetails(tmdbId: number) {
      return tmdbFetch(`/movie/${tmdbId}`, TMDBMovieDetails);
    },

    async getMovieCredits(tmdbId: number) {
      return tmdbFetch(`/movie/${tmdbId}/credits`, TMDBCredits);
    },

    getImageUrl(path: string, size: "w300" | "w500" | "w780" | "w1280" | "original" = "w500") {
      if (!path) return "";
      return `${imageBaseUrl}/${size}${path}`;
    },
  };
}

// Enhanced content types with TMDB data
export interface EnrichedTitle {
  // Existing fields
  id: string;
  slug: string;
  name: string;
  synopsis?: string;
  year?: number;
  durationSec?: number;
  posterUrl?: string;
  heroUrl?: string;

  // TMDB enriched fields
  tmdbId?: number;
  runtimeMinutes?: number;
  directorName?: string;
  castNames?: string[];
  genres?: string[];
  posterUrlHd?: string;
  backdropUrlHd?: string;
  voteAverage?: number;
  productionCompanies?: string[];
}
